'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Body, Button, I, Row } from '@leanin/ui';

export type SaveBarProps = {
  /** Whether the form has unsaved changes. Drives visibility + button enablement. */
  dirty: boolean;
  /** Save mutation in flight. Disables buttons and swaps label to "Saving…". */
  saving: boolean;
  /** Optional error to surface alongside the buttons. Replaces the dirty indicator. */
  error?: string | null;
  /** Save action. */
  onSave: () => void;
  /** Optional discard action — when supplied, a Discard button appears alongside Save. */
  onDiscard?: () => void;
  /** Save button label. Defaults to the localized "Save changes". */
  saveLabel?: string;
  /** Dirty-state indicator copy. Defaults to the localized "You have unsaved changes". */
  dirtyMessage?: string;
};

// Fixed-bottom save bar for admin/settings forms. Pinned to the viewport
// bottom whenever the form has unsaved changes, surfaces validation/mutation
// errors next to the action, and unmounts on a clean save (after a 1.5s
// "Saved." flash). Returning null when nothing needs communicating keeps
// clean admin pages free of empty save chrome.
//
// `position: fixed` (not sticky) so the bar reaches viewport bottom even on
// short forms that don't overflow the AdminShell's nested scroll container.
// Full-bleed: spans the whole viewport so it stays put across pages with
// different content widths. Inner content is constrained to the same
// max-width as AppShell's padded column so the buttons sit under their form.
export function SaveBar({
  dirty,
  saving,
  error,
  onSave,
  onDiscard,
  saveLabel,
  dirtyMessage,
}: SaveBarProps) {
  const t = useTranslations('Common');
  const saveLabelText = saveLabel ?? t('widgets.saveBar.save');
  const dirtyMessageText = dirtyMessage ?? t('widgets.saveBar.unsaved');
  const [justSaved, setJustSaved] = useState(false);
  const wasSaving = useRef(saving);
  const hasError = error !== null && error !== undefined && error !== '';

  useEffect(() => {
    const finishedSave = wasSaving.current && !saving;
    wasSaving.current = saving;
    if (!finishedSave) return;
    if (hasError || dirty) return;
    setJustSaved(true);
    const t = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(t);
  }, [saving, hasError, dirty]);

  useEffect(() => {
    if (dirty) setJustSaved(false);
  }, [dirty]);

  const visible = dirty || saving || hasError || justSaved;
  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label={t('widgets.saveBar.regionLabel')}
      style={{
        position: 'fixed',
        bottom: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        zIndex: 'var(--z-sticky)',
        background: 'var(--background)',
        borderTop: '1px solid var(--border)',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- position:fixed bar needs role/aria-label that Box doesn't pass through
        paddingBlock: 'var(--space-md)',
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- same reason as paddingBlock above
        paddingInline: 'var(--space-xl)',
      }}
    >
      <Row
        justify="space-between"
        align="center"
        gap="lg"
        style={{ maxWidth: '80rem', marginInline: 'auto' }}
      >
        <div style={{ minWidth: 0, flex: '1 1 auto' }} aria-live="polite">
          {hasError ? (
            <Body size="sm" color="destructive">{error}</Body>
          ) : justSaved ? (
            <Body size="sm" color="success">{t('widgets.saveBar.saved')}</Body>
          ) : dirty ? (
            <Body size="sm" color="mute">{dirtyMessageText}</Body>
          ) : null}
        </div>
        <Row gap="sm" align="center">
          {onDiscard !== undefined ? (
            <Button
              variant="ghost"
              size="md"
              onClick={onDiscard}
              disabled={saving || !dirty}
            >
              {t('widgets.saveBar.discard')}
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="md"
            icon={I.check}
            onClick={onSave}
            disabled={!dirty}
            loading={saving}
          >
            {saveLabelText}
          </Button>
        </Row>
      </Row>
    </div>
  );
}
